import * as pc from "playcanvas";
import { MOVEMENT_FPS, getSurfaceY, resolveAabbCollisions, GLASS_PANE_AABB } from "@hupper/shared";
import { ACTION } from "@hupper/shared/protocol";
import { getWeapon, weaponSlotFor } from "@hupper/shared/weapons";
import type { GameClient, InputFrame } from "../net/GameClient";
import type { AABB, Ramp } from "../scene/Arena";
import { loadSettings, subscribeSettings } from "../store/settingsStore";
import { isTouchMode } from "../input/touchMode";

/**
 * Minimum downward speed (m/s) at ground contact to count as a "landing"
 * worth a sound. A full jump impacts at ~JUMP_V0 (9.1 m/s); this filters out
 * tiny drops off ramps / curbs (a ~0.3 m fall) so the thud only fires on real
 * jumps and falls. Consumed by the audio layer via `consumeJustLanded`.
 */
const LAND_SOUND_MIN_SPEED = 4.0;

/**
 * FirstPersonController — local player control with optional networking.
 *
 * Sprint 0.3 / FPS-5:
 *   ✓ Client-side prediction (horizontal + vertical)
 *   ✓ Jump latch — never drops a quick Space tap
 *   ✓ AABB wall collision (XZ push-out, skips boxes the player stands on)
 *   ✓ Ramp / elevated-surface system — player walks up ramps naturally
 *   ✓ Server snapshot soft-reconciliation
 *   ✓ Shift walk · Ctrl crouch (speed + camera; server matches for prediction)
 */
export class FirstPersonController {
  private app: pc.AppBase;
  private body: pc.Entity;
  private camera: pc.Entity;
  private canvas: HTMLCanvasElement;
  private client: GameClient | null;

  private locked = false;
  /** Touch devices (iPad / phones) have no Pointer Lock API — `locked` is
   *  emulated there: true whenever no click-driven menu owns the input
   *  (recomputed every frame in `update()`). Aim comes from the touch
   *  look-drag via {@link applyLookDelta}; fire from the on-screen button
   *  (or a trackpad click on the canvas — Magic Keyboard). */
  private readonly touchMode = isTouchMode();

  private yaw   = 0;
  private pitch  = 0;
  /** Default degrees-per-pixel feel, scaled by the player's sensitivity
   *  multiplier from the settings store (1.0× = this base). */
  private static readonly BASE_SENSITIVITY = 0.12;
  private mouseSensitivity = FirstPersonController.BASE_SENSITIVITY;
  private readonly pitchLimit       = 85;

  private accDPitch = 0;
  private accDYaw   = 0;

  private keys = {
    forward: false,
    back: false,
    left: false,
    right: false,
    jump: false,
    /** Shift — walk speed + future footstep: silent (no run SFX) when implemented. */
    walk: false,
    /** Ctrl — crouch (slower, lower camera; future weapon accuracy — see MOVEMENT_FPS). */
    crouch: false,
  };

  /* ── Movement sources (keyboard ∪ touch) ──────────────────────────
     The keyboard and the touch joystick/JUMP button each track their own
     held-state; `keys.forward/back/left/right/jump` is the per-field OR
     of the two (mergeMoveKeys). Writing straight into `keys` from both
     made them fight — releasing the joystick cleared a held W, a W keyup
     cancelled the held joystick, and just RESTING a thumb on the left
     screen edge (holding the iPad) kept re-clearing WASD through the
     joystick dead zone. Hybrid play (iPad + Magic Keyboard: WASD/Space
     to move, touch to aim) needs both live at once. */
  private kbMove    = { forward: false, back: false, left: false, right: false, jump: false };
  private touchHeld = { forward: false, back: false, left: false, right: false, jump: false };

  private mergeMoveKeys(): void {
    this.keys.forward = this.kbMove.forward || this.touchHeld.forward;
    this.keys.back    = this.kbMove.back    || this.touchHeld.back;
    this.keys.left    = this.kbMove.left    || this.touchHeld.left;
    this.keys.right   = this.kbMove.right   || this.touchHeld.right;
    this.keys.jump    = this.kbMove.jump    || this.touchHeld.jump;
  }

  /** LMB held — auto weapons keep sending SHOOT every frame while held. */
  private fireHeld = false;
  /** Edge-triggered SHOOT for semi-auto: latch on mousedown, clear after one frame. */
  private fireEdge = false;
  /** Edge-triggered RELOAD (R). */
  private reloadEdge = false;
  /** Edge-triggered knife LMB slash. */
  private knifeSlashEdge = false;
  /** Edge-triggered knife RMB stab. */
  private knifeStabEdge = false;

  /* ── AWP scope (CS:GO-style) ──────────────────────────────────────
     RMB cycles unscoped → zoom1 → zoom2 → unscoped while a sniper is
     equipped. Firing scoped drops the scope for the bolt cycle and
     re-zooms automatically. While zoomed the client sends ACTION.ZOOM
     every frame — the server reads it to drop the noscope penalty. */
  /** Hip-fire FOV (deg). */
  private static readonly BASE_FOV = 75;
  /**
   * FOV per zoom level. Matches CS:GO AWP magnification ratios
   * (90→40 first zoom, 90→10 second zoom) scaled to our 75° base.
   */
  private static readonly ZOOM_FOVS = [75, 33, 9] as const;
  private zoomLevel: 0 | 1 | 2 = 0;
  /** Smoothed camera FOV — lerps toward ZOOM_FOVS[zoomLevel]. */
  private currentFov = FirstPersonController.BASE_FOV;
  /** Zoom level to restore after the bolt cycle (0 = none pending). */
  private pendingRezoomLevel: 0 | 1 | 2 = 0;
  private rezoomAtMs = 0;
  /** Fired on each manual scope toggle (RMB) — drives the zoom SFX. */
  private zoomCbs: Array<() => void> = [];
  /**
   * Last non-knife weapon equipped, captured every snapshot. `Q` swaps
   * to knife if not equipped, otherwise back to this id.
   */
  private lastNonKnifeWeaponId: string | null = null;

  /**
   * Latch set on keydown (Space), cleared after the physics frame consumes it.
   * Prevents missed jumps when keydown + keyup both fire between two animation frames.
   */
  private jumpRequested = false;

  private inputSeq    = 0;
  private vy          = 0;
  private coyote      = 0;
  private prevJumpHeld = false;

  /* ── Input send batching ──────────────────────────────────────────
     The server simulates at 64 Hz and coalesces inputs between ticks
     (latest-wins), so sending one input per RENDER frame (up to 144-240
     msgs/s on high-refresh displays) is pure overhead — and the extra
     packets can queue up on weak links, adding latency spikes. Inputs
     are sent at ≤64 Hz instead; combat edges (shoot / knife / reload)
     and jump presses flush IMMEDIATELY so action latency is unchanged. */
  private lastInputSendMs = 0;
  private static readonly INPUT_SEND_INTERVAL_MS = 1000 / 64 - 1;
  /** Set on the Space keydown edge; forces the next frame to send. */
  private jumpUrgent = false;

  /* ── Client prediction history (server reconciliation) ────────────
     Snapshot of the predicted body position recorded at each SENT input
     seq. When the server acks `lastProcessedInputSeq = N`, its position
     is compared against OUR position at that same seq — an apples-to-
     apples comparison on the same point of the input timeline. The old
     code compared the server position (always ~RTT + snapshot-interval
     in the past) against the CURRENT predicted position, which made the
     "error" proportional to latency × speed — the reconciliation lerp
     then dragged the sprinting player backwards every snapshot (the
     rubber-band / "movement returns" feel), and any ping spike tripped
     the 1.5 m hard-snap teleport. Anchored on the acked seq, the error
     is ~0 in normal play regardless of ping, so prediction stays crisp
     and corrections only fire on REAL divergence. */
  private predHistory: Array<{ seq: number; x: number; y: number; z: number }> = [];
  private static readonly PRED_HISTORY_MAX = 256;
  /** Acks at or below this seq predate a teleport (respawn / hard snap)
   *  and must be ignored — their recorded positions are pre-teleport. */
  private ignoreAcksThroughSeq = 0;
  /** Horizontal error (m) below which the prediction is considered
   *  correct — absorbs the ≤1-tick integration-cadence skew between the
   *  client's per-frame steps and the server's per-tick steps. */
  private static readonly RECONCILE_DEADZONE_M = 0.15;
  /** Fraction of a real divergence corrected per snapshot (smooth). */
  private static readonly RECONCILE_CORRECT_FRACTION = 0.35;
  /** Max metres applied in a single snapshot's correction step. */
  private static readonly RECONCILE_MAX_STEP_M = 0.35;
  /** Anchored error beyond this is a teleport we somehow missed — snap. */
  private static readonly RECONCILE_SNAP_M = 3.0;

  /** Velocity of the local body this frame (m/s) — horizontal move intent
   *  plus vertical physics. Read by GrenadeController so thrown grenades
   *  inherit player momentum (CS-style running/jump throws). */
  private readonly lastVelocity = new pc.Vec3();
  /** Set the frame a jump fires; consumed by the local shadow to play the
   *  jump animation immediately on the Space press (not when airborne). */
  private justJumped  = false;
  /** True while the local player is off the ground. Drives the landing edge:
   *  a `true → grounded` transition with real impact speed sets `justLanded`. */
  private airborne    = false;
  /** Set the frame the player lands hard from a jump/fall; consumed by the
   *  audio layer to play a one-shot landing sound. */
  private justLanded  = false;

  private hasInitialServerPos = false;

  private collisionBoxes: AABB[] = [];
  private ramps: Ramp[]          = [];

  /** True once the T-room glass has shattered this round (server-driven via
   *  {@link setGlassBroken}). While intact, the glass pane joins the
   *  horizontal push-out list so the body can't walk/mantle through the
   *  window — mirrors the server's glass-aware `liveAabbs`. */
  private glassBroken = false;
  /** Effective horizontal collision list: `collisionBoxes` (+ the intact
   *  glass pane). `getSurfaceY` keeps using the glass-free `collisionBoxes`
   *  so nobody stands on top of the pane. Rebuilt on glass-state change. */
  private wallBoxes: AABB[] = [];

  /** Smoothed camera Y local (standing ↔ crouch). */
  private cameraHeight = MOVEMENT_FPS.EYE_HEIGHT_STANDING;

  /**
   * Transient camera recoil — random per-shot kick for pistols / AWP /
   * SMGs. Decays back to 0 within ~250 ms.
   */
  private recoilPitch = 0;
  private recoilYaw = 0;
  /**
   * Sprint 3 — deterministic pattern-driven kick for AK / M4 family.
   * Holds at the cumulative pattern offset while spraying, only decays
   * after the 400 ms spray reset elapses.
   */
  private patternPitch = 0;
  private patternYaw = 0;
  private lastFireMs = 0;

  /* ── Sprint 3 — spray pattern + inaccuracy tracking (client mirror) ──
     Keeps the local crosshair gap in sync with the server-computed
     bullet spread. Resets on weapon switch + after 400 ms of no fire,
     identical to FpsArena.advanceSpray / decay. */
  private sprayIndex = 0;
  private sprayInaccDeg = 0;
  private lastShotForSprayMs = 0;
  /** Weapon id snapshot — resets spray state when this changes. */
  private lastWeaponIdForSpray: string | null = null;
  /** Last computed inaccuracy cone (degrees). Read by Crosshair UI. */
  private inaccuracyDeg = 0;

  /**
   * Sprint 5 — external source of camera shake (grenade explosions,
   * damage taken). `consumeShake()` returns the current pitch/yaw
   * offsets every frame; the shake bus self-decays.
   */
  private shakeSource: { consumeShake(): { pitch: number; yaw: number } } | null = null;

  /**
   * Sprint 5 — external "is the view-model in a state that should block
   * combat input?" predicate. Wired to LocalWeaponView.isBusy() in
   * main.ts so weapon switch + inspect animations lock LMB/RMB just
   * like in CS:GO.
   */
  private inputBlocker: (() => boolean) | null = null;

  constructor(
    app: pc.AppBase,
    canvas: HTMLCanvasElement,
    client: GameClient | null = null,
    collisionBoxes: AABB[] = [],
    ramps: Ramp[] = [],
  ) {
    this.app           = app;
    this.canvas        = canvas;
    this.client        = client;
    this.collisionBoxes = collisionBoxes;
    this.ramps         = ramps;
    this.rebuildWallBoxes(); // glass intact at round start → pane blocks the body

    this.body = new pc.Entity("player-body");
    // Default offline spawn: T side at (12, 0, 0) facing -X (yaw = -90)
    this.body.setPosition(12, MOVEMENT_FPS.FLOOR_Y, 0);
    this.yaw = -90;
    this.app.root.addChild(this.body);

    this.camera = new pc.Entity("player-camera");
    this.camera.addComponent("camera", {
      clearColor: new pc.Color(0.52, 0.72, 0.90), // sky-blue horizon
      fov: FirstPersonController.BASE_FOV,
      nearClip: 0.05,
      farClip: 200,
    });
    this.camera.setLocalPosition(0, MOVEMENT_FPS.EYE_HEIGHT_STANDING, 0);
    this.body.addChild(this.camera);

    // Mouse sensitivity = base × the player's saved multiplier; re-applied
    // live whenever they drag the settings slider.
    const applySensitivity = (mult: number) => {
      this.mouseSensitivity = FirstPersonController.BASE_SENSITIVITY * mult;
    };
    applySensitivity(loadSettings().sensitivity);
    subscribeSettings((s) => applySensitivity(s.sensitivity));

    this.attachInputHandlers();
    this.attachUpdateLoop();
    this.attachLockListeners();
  }

  // ── Input ──────────────────────────────────────────────────────────────────

  private attachInputHandlers(): void {
    const onKeyDown = (e: KeyboardEvent) => {
      // While the chat input is up the keyboard belongs to it — no movement,
      // drop or pickup. Mouse aim/fire stays live (CS:GO chat behavior) and
      // keyup below stays ungated so a key held across the open can't stick.
      if (document.body.classList.contains("chat-open")) return;
      switch (e.code) {
        case "KeyW": this.kbMove.forward = true; break;
        case "KeyS": this.kbMove.back    = true; break;
        case "KeyA": this.kbMove.left    = true; break;
        case "KeyD": this.kbMove.right   = true; break;
        case "ShiftLeft":
        case "ShiftRight":
          this.keys.walk = e.shiftKey;
          break;
        case "ControlLeft":
        case "ControlRight":
          this.keys.crouch = e.ctrlKey;
          break;
        case "Space":
          if (!e.repeat) {
            this.kbMove.jump = true;
            this.jumpRequested = true;
            this.jumpUrgent = true; // flush this press on the wire this frame
          }
          e.preventDefault();
          break;
        case "KeyG":
          // Drop the equipped weapon (server validates ownership + slot).
          if (!e.repeat) this.client?.sendDropWeapon();
          break;
        case "KeyE":
          // Try to pick up whatever's under the crosshair (server raycasts).
          if (!e.repeat) this.client?.sendPickupWeapon();
          break;
      }
      this.mergeMoveKeys();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      switch (e.code) {
        case "KeyW": this.kbMove.forward = false; break;
        case "KeyS": this.kbMove.back    = false; break;
        case "KeyA": this.kbMove.left    = false; break;
        case "KeyD": this.kbMove.right   = false; break;
        case "ShiftLeft":
        case "ShiftRight":
          this.keys.walk = e.shiftKey;
          break;
        case "ControlLeft":
        case "ControlRight":
          this.keys.crouch = e.ctrlKey;
          break;
        case "Space": this.kbMove.jump   = false; break;
      }
      this.mergeMoveKeys();
    };

    // Mouse buttons drive primary fire (LMB). RMB is per-weapon-class:
    // knife → stab variant, sniper → scope zoom cycle, others → no-op.
    const onMouseDown = (e: MouseEvent) => {
      if (!this.locked) return;
      // Touch mode: only canvas clicks fire (a real trackpad/mouse click —
      // Magic Keyboard). Taps on the on-screen buttons / HUD synthesize
      // mouse events targeting THOSE elements; letting them through would
      // shoot a bullet on every UI tap.
      if (this.touchMode && e.target !== this.canvas) return;
      if (e.button === 0) {
        this.fireHeld = true;
        this.fireEdge = true;
        if (this.isMeleeEquipped()) this.knifeSlashEdge = true;
        e.preventDefault();
      } else if (e.button === 2) {
        if (this.isMeleeEquipped()) {
          this.knifeStabEdge = true;
        } else if (this.isSniperEquipped()) {
          this.cycleZoom();
        }
        e.preventDefault();
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 0) this.fireHeld = false;
    };
    // Block the browser context menu so RMB-stab feels native.
    const onContextMenu = (e: MouseEvent) => {
      if (this.locked) e.preventDefault();
    };
    window.addEventListener("contextmenu", onContextMenu);

    const onReloadKey = (e: KeyboardEvent) => {
      if (e.code !== "KeyR") return;
      // Never hijack browser shortcuts: Ctrl/Cmd+R reloads the page,
      // Ctrl+Shift+R hard-reloads. Without this guard, the in-game reload
      // would also call preventDefault() and the browser refresh would
      // never fire — extremely confusing during dev.
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      // Only reload while actually playing (lock held, no menu/chat up).
      if (!this.locked) return;
      if (document.body.classList.contains("buy-open")) return;
      if (document.body.classList.contains("chat-open")) return;
      if (e.repeat) return;
      this.reloadEdge = true;
      e.preventDefault();
    };

    // Slot switcher: 1=primary (rifle/sniper), 2=pistol, 3=knife,
    // 4=grenade (cycles through owned grenades).
    const onSlotKey = (e: KeyboardEvent) => {
      if (!this.locked) return;
      if (document.body.classList.contains("chat-open")) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.repeat) return;
      let slot: 1 | 2 | 3 | 4 | 0 = 0;
      switch (e.code) {
        case "Digit1": slot = 1; break;
        case "Digit2": slot = 2; break;
        case "Digit3": slot = 3; break;
        case "Digit4": slot = 4; break;
        default: return;
      }
      this.equipSlot(slot);
      e.preventDefault();
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!this.locked) return;
      // Touch mode: aim comes exclusively from the look-drag (see
      // TouchControls). iPad Safari still emits mouse events for a paired
      // trackpad but without pointer lock `movementX/Y` is unreliable
      // (undefined on some builds → NaN would poison yaw forever).
      if (this.touchMode) return;
      this.applyLookDelta(e.movementX, e.movementY);
    };

    // CS:GO `Q` quickswitch: knife ↔ last non-knife weapon.
    const onQuickSwitchKey = (e: KeyboardEvent) => {
      if (e.code !== "KeyQ") return;
      if (e.repeat) return;
      if (!this.locked) return;
      if (document.body.classList.contains("chat-open")) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      this.quickSwitchKnife();
      e.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    window.addEventListener("keyup",   onKeyUp,   { capture: true });
    window.addEventListener("keydown", onReloadKey, { capture: true });
    window.addEventListener("keydown", onSlotKey, { capture: true });
    window.addEventListener("keydown", onQuickSwitchKey, { capture: true });
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup",   onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
  }

  /**
   * Apply a look delta in mouse-pixel units — shared by the pointer-locked
   * mousemove path and the touch look-drag (TouchControls). Sensitivity is
   * the player's setting scaled by the scope FOV ratio (CS:GO
   * zoom_sensitivity_ratio 1.0).
   */
  applyLookDelta(dxPx: number, dyPx: number): void {
    const sens = this.mouseSensitivity * this.zoomSensitivityScale();
    const dYaw   = -dxPx * sens;
    const dPitch = -dyPx * sens;
    this.yaw   += dYaw;
    const prevPitch = this.pitch;
    this.pitch  = Math.max(-this.pitchLimit, Math.min(this.pitchLimit, this.pitch + dPitch));
    this.accDYaw   += dYaw;
    // Send the CLAMPED applied pitch delta, not the raw mouse delta.
    // The server accumulates these deltas and clamps per tick; if we
    // sent the raw delta, a fast flick that overshoots ±85° would push
    // the server's player.pitch past what the client actually applied
    // (the client clamped per-event, the server per-tick), leaving
    // player.pitch permanently above this.pitch. Shots fire from
    // player.pitch, so the bullet drifted above the crosshair and the
    // gap grew every time the player flicked to a pitch extreme.
    this.accDPitch += (this.pitch - prevPitch);
  }

  // ── Touch-mode input API (driven by input/TouchControls.ts) ──────────────

  /** Virtual joystick → the same 8-way movement booleans WASD sets.
   *  Kept in its own source and OR-merged with the keyboard so neither
   *  cancels the other (see mergeMoveKeys). */
  setTouchMove(forward: boolean, back: boolean, left: boolean, right: boolean): void {
    this.touchHeld.forward = forward;
    this.touchHeld.back    = back;
    this.touchHeld.left    = left;
    this.touchHeld.right   = right;
    this.mergeMoveKeys();
  }

  /** FIRE button — mirrors LMB down/up (grenade charge rides `fireHeld`,
   *  same as holding the mouse button). */
  setTouchFire(down: boolean): void {
    if (down) {
      if (!this.locked) return;
      this.fireHeld = true;
      this.fireEdge = true;
      if (this.isMeleeEquipped()) this.knifeSlashEdge = true;
    } else {
      this.fireHeld = false;
    }
  }

  /** ZOOM/alt button — mirrors RMB: sniper scope cycle, knife stab. */
  touchAltAction(): void {
    if (!this.locked) return;
    if (this.isMeleeEquipped()) {
      this.knifeStabEdge = true;
    } else if (this.isSniperEquipped()) {
      this.cycleZoom();
    }
  }

  /** JUMP button — mirrors the Space edge + latch semantics. */
  setTouchJump(down: boolean): void {
    this.touchHeld.jump = down;
    if (down) {
      this.jumpRequested = true;
      this.jumpUrgent = true;
    }
    this.mergeMoveKeys();
  }

  /** Reload button — mirrors the R key's gates. */
  touchReload(): void {
    if (!this.locked) return;
    if (document.body.classList.contains("chat-open")) return;
    this.reloadEdge = true;
  }

  /**
   * Drop every held movement key. Called when the chat input opens: from
   * that moment keydown is gated, so the eventual keyup of a key held
   * across the open would be the only thing clearing its flag — but if the
   * player tabs away mid-chat that keyup never lands and they'd run
   * forever. Clearing up-front matches CS:GO (typing stops you).
   */
  releaseHeldKeys(): void {
    // Chat owns the KEYBOARD — drop only that source. A live joystick
    // touch keeps moving (its keyup can't be starved by the chat gate;
    // touch events aren't keyboard events).
    this.kbMove.forward = false;
    this.kbMove.back = false;
    this.kbMove.left = false;
    this.kbMove.right = false;
    this.kbMove.jump = false;
    this.keys.walk = false;
    this.keys.crouch = false;
    this.jumpRequested = false;
    this.jumpUrgent = false;
    this.mergeMoveKeys();
  }

  private isMeleeEquipped(): boolean {
    if (!this.client) return false;
    const id = this.client.getLatestSelf()?.weaponId ?? "";
    try {
      return getWeapon(id).class === "melee";
    } catch {
      return false;
    }
  }

  /** True while a scoped sniper (AWP) is in hands. Read by the Crosshair
   *  UI — snipers have no hip crosshair, exactly like CS:GO. */
  isSniperEquipped(): boolean {
    if (!this.client) return false;
    const id = this.client.getLatestSelf()?.weaponId ?? "";
    try {
      return getWeapon(id).class === "sniper";
    } catch {
      return false;
    }
  }

  /** True while the scope is up (any zoom level). Read by ScopeOverlay +
   *  LocalWeaponView (viewmodel hides while scoped). */
  isScoped(): boolean { return this.zoomLevel > 0; }

  /** Current scope stage: 0 = hip, 1 = first zoom, 2 = second zoom. */
  getZoomLevel(): number { return this.zoomLevel; }

  /** RMB on a sniper: unscoped → zoom1 → zoom2 → unscoped. A manual
   *  toggle always cancels any pending bolt-cycle re-zoom. */
  private cycleZoom(): void {
    this.pendingRezoomLevel = 0;
    this.rezoomAtMs = 0;
    this.zoomLevel = this.zoomLevel === 0 ? 1 : this.zoomLevel === 1 ? 2 : 0;
    for (const cb of this.zoomCbs) cb();
  }

  /** Subscribe to manual scope toggles (RMB) — drives the zoom SFX. */
  onZoom(cb: () => void): () => void {
    this.zoomCbs.push(cb);
    return () => { this.zoomCbs = this.zoomCbs.filter((c) => c !== cb); };
  }

  private resetZoom(): void {
    this.zoomLevel = 0;
    this.pendingRezoomLevel = 0;
    this.rezoomAtMs = 0;
  }

  /** CS:GO zoom_sensitivity_ratio 1.0 — sensitivity scales with FOV. */
  private zoomSensitivityScale(): number {
    if (this.zoomLevel === 0) return 1;
    return (
      FirstPersonController.ZOOM_FOVS[this.zoomLevel] /
      FirstPersonController.BASE_FOV
    );
  }

  /**
   * Per-frame scope upkeep: drop the scope when the sniper leaves the
   * hands or pointer-lock is lost, restore the zoom level after the
   * bolt cycle, and lerp the camera FOV toward the target.
   */
  private updateZoom(dt: number): void {
    if (this.zoomLevel > 0 || this.pendingRezoomLevel > 0) {
      if (!this.locked || !this.isSniperEquipped()) this.resetZoom();
    }
    // Bolt-cycle re-zoom — CS:GO: a scoped shot unscopes during the
    // bolt, then the scope comes back automatically.
    if (this.pendingRezoomLevel > 0 && performance.now() >= this.rezoomAtMs) {
      this.zoomLevel = this.pendingRezoomLevel;
      this.pendingRezoomLevel = 0;
    }
    const targetFov = FirstPersonController.ZOOM_FOVS[this.zoomLevel];
    this.currentFov += (targetFov - this.currentFov) * Math.min(1, dt * 18);
    if (Math.abs(this.currentFov - targetFov) < 0.05) this.currentFov = targetFov;
    const cam = this.camera.camera;
    if (cam && cam.fov !== this.currentFov) cam.fov = this.currentFov;
  }

  private quickSwitchKnife(): void {
    if (!this.client) return;
    const self = this.client.getLatestSelf();
    if (!self) return;
    let target: string | null = null;
    if (this.isMeleeEquipped()) {
      // Knife → last non-knife.
      target = this.lastNonKnifeWeaponId;
    } else {
      // Anything → knife. Always available — knife is a guaranteed starter.
      target = "knife";
    }
    if (!target || target === self.weaponId) return;
    this.client.sendWeaponEquip(target);
  }

  private attachLockListeners(): void {
    // Touch mode (iPad / phones): no Pointer Lock API exists — Safari on
    // iOS/iPadOS never exposes requestPointerLock. The lock is emulated
    // instead: `locked` is recomputed every frame in update() as "no
    // click-driven menu is open", so all the lock-gated input (fire, slot
    // keys, reload, quickswitch, footsteps) works untouched.
    if (this.touchMode) {
      this.locked = !FirstPersonController.touchMenuOpen();
      return;
    }

    const requestLock = () => this.canvas.requestPointerLock();

    // No intro panel — clicking the canvas (anywhere on screen) acquires
    // pointer-lock directly. Suppressed while the buy menu is open so a
    // stray click on the dimmed area doesn't silently re-grab the lock
    // and pin the cursor to canvas centre, stealing buy-button clicks.
    this.canvas.addEventListener("click", () => {
      if (document.body.classList.contains("buy-open")) return;
      // Settings panel open → keep the cursor free for the sliders.
      if (document.body.classList.contains("settings-open")) return;
      if (!this.locked) requestLock();
    });
    document.addEventListener("pointerlockchange", () => {
      this.locked = document.pointerLockElement === this.canvas;
    });
  }

  /** Menus that own the cursor/taps — while any is up the emulated touch
   *  lock releases, exactly like the desktop flow exits pointer lock.
   *  (chat-open is NOT here: desktop keeps fire live during chat too.) */
  private static touchMenuOpen(): boolean {
    const c = document.body.classList;
    return (
      c.contains("buy-open") ||
      c.contains("settings-open") ||
      c.contains("xh-settings-open")
    );
  }

  private attachUpdateLoop(): void {
    this.app.on("update", (dt: number) => this.update(dt));
  }

  // ── Ramp / elevated surface helpers ───────────────────────────────────────

  /**
   * Returns the highest surface Y under the player's feet considering:
   *   1. Static AABB tops (cover crates, catwalk plates, mid platform —
   *      anything whose XZ footprint overlaps the player capsule and whose
   *      top is at or below the player's current y)
   *   2. Ramp / inclined-platform surfaces (MAP_RAMPS)
   *   3. The world floor as a fallback
   *
   * Without (1), AABB cover crates were not "walkable" — gravity dragged
   * the player down into the box's Y range and the lateral push-out then
   * shoved them off the side. (See "blocks pushing player off" bug.)
   */
  /**
   * Delegates to the shared `getSurfaceY` so client + server compute
   * the effective floor with the exact same algorithm. The data
   * sources (`this.collisionBoxes` / `this.ramps`) still come from
   * `Arena.ts` here — they mirror the canonical lists in
   * `packages/shared/src/arenaGeometry.ts` and are kept in sync
   * manually as part of any geometry change.
   */
  private getSurfaceY(pos: pc.Vec3): number {
    return getSurfaceY(
      pos.x,
      pos.z,
      pos.y,
      MOVEMENT_FPS.PLAYER_RADIUS,
      this.collisionBoxes,
      this.ramps,
      MOVEMENT_FPS.FLOOR_Y,
    );
  }

  // ── Vertical physics ───────────────────────────────────────────────────────

  /** Integrates gravity, jump, and ramp/floor clamping. */
  private integrateVertical(dt: number, jumpHeld: boolean, pos: pc.Vec3): void {
    const { GRAVITY, JUMP_V0, GROUND_EPS, COYOTE_SEC } = MOVEMENT_FPS;

    const floorY  = this.getSurfaceY(pos);
    const grounded = pos.y <= floorY + GROUND_EPS && this.vy <= 0;

    if (grounded) {
      this.touchDown();          // latch the landing edge before vy is zeroed
      pos.y      = floorY;
      this.vy    = 0;
      this.coyote = COYOTE_SEC;
    } else {
      this.coyote = Math.max(0, this.coyote - dt);
    }

    const jumpEdge = jumpHeld && !this.prevJumpHeld && !this.keys.crouch;
    this.prevJumpHeld = jumpHeld;
    if (jumpEdge && (grounded || this.coyote > 0)) {
      this.vy     = JUMP_V0;
      this.coyote = 0;
      this.justJumped = true;
      this.airborne = true;      // leaving the ground under our own power
    }

    if (!grounded || this.vy > 0) {
      this.vy -= GRAVITY * dt;
    }

    pos.y += this.vy * dt;

    // Hard floor clamp (floor or ramp surface). Also catches fast falls that
    // overshoot below the floor in a single frame — the `grounded` check above
    // misses those, so the landing edge is latched here too.
    if (pos.y < floorY) {
      this.touchDown();
      pos.y   = floorY;
      this.vy = 0;
    } else if (pos.y > floorY + GROUND_EPS) {
      this.airborne = true;      // off the ground (jump rise, or walked off a ledge)
    }
  }

  /** Resolve a ground contact. If we were airborne and hit the surface with
   *  real downward speed, latch a one-shot landing event for the audio layer
   *  (consumed via `consumeJustLanded`). Reads `this.vy` BEFORE it is zeroed. */
  private touchDown(): void {
    if (this.airborne && this.vy <= -LAND_SOUND_MIN_SPEED) this.justLanded = true;
    this.airborne = false;
  }

  // ── AABB wall collision ────────────────────────────────────────────────────

  /**
   * XZ push-out against static AABB obstacles. Delegates to the shared
   * `resolveAabbCollisions` so server `FpsArena.tick` and client run
   * the exact same algorithm — without parity, snapshot reconciliation
   * yanks the client into walls the server walked through.
   */
  private rebuildWallBoxes(): void {
    this.wallBoxes = this.glassBroken
      ? this.collisionBoxes
      : [...this.collisionBoxes, GLASS_PANE_AABB];
  }

  /**
   * Server-driven glass state (the `glass_break` event + snapshot self-heal
   * + round reset, wired in main.ts). Toggles whether the intact pane blocks
   * the body, keeping client prediction in parity with the server's
   * glass-aware movement so reconciliation never yanks the player through.
   */
  public setGlassBroken(broken: boolean): void {
    if (this.glassBroken === broken) return;
    this.glassBroken = broken;
    this.rebuildWallBoxes();
  }

  private resolveCollisions(pos: pc.Vec3): void {
    const ph = this.keys.crouch
      ? MOVEMENT_FPS.PLAYER_HEIGHT_CROUCH
      : MOVEMENT_FPS.PLAYER_HEIGHT_STANDING;
    const buf = { x: pos.x, y: pos.y, z: pos.z };
    const { steppedUp } = resolveAabbCollisions(
      buf,
      ph,
      MOVEMENT_FPS.PLAYER_RADIUS,
      this.wallBoxes,
      MOVEMENT_FPS.STEP_HEIGHT,
    );
    pos.x = buf.x;
    pos.z = buf.z;
    // A mantle raises the feet onto a ledge — adopt the new height and
    // kill downward velocity so the player settles on top instead of
    // immediately falling back off the side.
    if (steppedUp) {
      pos.y = buf.y;
      if (this.vy < 0) this.vy = 0;
    }
  }

  // ── Main update ────────────────────────────────────────────────────────────

  private update(dt: number): void {
    // Touch mode: the emulated lock tracks menu state per frame (menus
    // toggle body classes; there's no pointerlockchange to listen to).
    if (this.touchMode) {
      const wantLock = !FirstPersonController.touchMenuOpen();
      if (!wantLock && this.locked) this.fireHeld = false; // menu opened mid-press
      this.locked = wantLock;
    }

    // Transient recoil (pistols/SMG/AWP) — fast decay (~250 ms).
    const recoilDecay = Math.min(1, dt * 8);
    this.recoilPitch *= 1 - recoilDecay;
    this.recoilYaw *= 1 - recoilDecay;
    if (Math.abs(this.recoilPitch) < 0.01) this.recoilPitch = 0;
    if (Math.abs(this.recoilYaw) < 0.005) this.recoilYaw = 0;

    // Sprint 3 — pattern kick (AK / M4 family) HOLDS while the spray is
    // active so the deterministic pattern is visible. Decays only after
    // the 400 ms reset (no shot for >400 ms).
    const sprayIdle = performance.now() - this.lastShotForSprayMs > 400;
    if (sprayIdle && (this.patternPitch !== 0 || this.patternYaw !== 0)) {
      // Smooth recovery to neutral over ~250 ms once the spray resets.
      const patternDecay = Math.min(1, dt * 8);
      this.patternPitch *= 1 - patternDecay;
      this.patternYaw *= 1 - patternDecay;
      if (Math.abs(this.patternPitch) < 0.02) this.patternPitch = 0;
      if (Math.abs(this.patternYaw) < 0.01) this.patternYaw = 0;
    }

    // Client mirrors the server's spray index reset + bloom decay.
    this.updateInaccuracy(dt);

    // AWP scope — FOV lerp, bolt-cycle re-zoom, forced unscope on
    // weapon switch / lock loss.
    this.updateZoom(dt);

    // Sprint 5 — camera shake from GrenadeEffects shake bus (grenade
    // explosions, damage taken). The bus self-decays each frame.
    let shakePitch = 0;
    let shakeYaw = 0;
    if (this.shakeSource) {
      const s = this.shakeSource.consumeShake();
      shakePitch = s.pitch;
      shakeYaw = s.yaw;
    }

    const totalRecoilPitch = this.recoilPitch + this.patternPitch + shakePitch;
    const totalRecoilYaw = this.recoilYaw + this.patternYaw + shakeYaw;
    // Body yaws only with the player's intent (`this.yaw`). Pattern,
    // recoil and shake yaws live on the camera's LOCAL rotation so
    // they're purely cosmetic. Pre-fix `body.setEulerAngles` baked all
    // of them into the body, which then contaminated `body.forward` /
    // `body.right` used below for WASD direction — pressing W during
    // an AK spray or right after a grenade explosion / damage shake
    // sent the player on a diagonal instead of straight forward.
    this.body.setEulerAngles(0, this.yaw, 0);
    this.camera.setLocalEulerAngles(this.pitch + totalRecoilPitch, totalRecoilYaw, 0);

    const eyeTarget = this.keys.crouch
      ? MOVEMENT_FPS.EYE_HEIGHT_CROUCH
      : MOVEMENT_FPS.EYE_HEIGHT_STANDING;
    const camLerp = Math.min(1, dt * 14);
    this.cameraHeight += (eyeTarget - this.cameraHeight) * camLerp;
    this.camera.setLocalPosition(0, this.cameraHeight, 0);

    const pos = this.body.getPosition().clone();

    // Freezetime lock. During `buy` / `end` the server doesn't integrate
    // position, so local prediction must match or the player slides forward
    // and gets snapped back by reconciliation. `between_rounds` is the
    // post-round loot window — movement stays UNLOCKED there, but only while
    // alive (a dead player is spectating and shouldn't predict body motion).
    // Look-around already updated above; we just gate horizontal + vertical.
    const matchPhase = this.client?.getMatchPhase() ?? "live";
    const selfAlive = (this.client?.getLatestSelf()?.health ?? 100) > 0;
    const movementUnlocked =
      selfAlive &&
      (matchPhase === "live" || matchPhase === "warmup" || matchPhase === "between_rounds");
    // `jumpThisFrame` is consumed below by the input frame builder
    // — needs to exist regardless of phase. We default it to false in
    // freezetime so the network sees "no jump".
    let jumpThisFrame = false;

    this.lastVelocity.set(0, 0, 0);
    if (movementUnlocked) {
      // Horizontal movement
      const forward = this.body.forward.clone();
      const right   = this.body.right.clone();
      forward.y = 0; forward.normalize();
      right.y   = 0; right.normalize();

      const dir = new pc.Vec3();
      if (this.keys.forward) dir.add(forward);
      if (this.keys.back)    dir.sub(forward);
      if (this.keys.right)   dir.add(right);
      if (this.keys.left)    dir.sub(right);
      if (dir.lengthSq() > 0) {
        const moving = !!(this.keys.forward || this.keys.back || this.keys.left || this.keys.right);
        // Scoped movement is walk-speed (CS:GO). Reuses the walk flag so
        // the server predicts the exact same speed — see the input frame
        // builder below, which also forces `walk` while scoped.
        const wantsWalk = (this.keys.walk || this.isScoped()) && moving;
        const wantsCrouch = this.keys.crouch && moving;
        // Per-weapon base mobility (m/s) — Sprint 1.3. Falls back to the
        // legacy constant when self/snapshot is not ready or weapon is
        // unknown. Knife > pistol > smg > rifle > shotgun > AWP.
        let baseMobility: number = MOVEMENT_FPS.H_MOVE_SPEED;
        const selfWeapon = this.client?.getLatestSelf()?.weaponId;
        if (selfWeapon) {
          try {
            baseMobility = getWeapon(selfWeapon).mobility;
          } catch {
            /* fall back */
          }
        }
        // Damage "tagging" (CS:GO) — server cuts our speed when we're shot
        // or knifed and recovers it over ~0.5–2 s. Read the authoritative
        // value from the latest snapshot and fold it into the predicted
        // speed so prediction matches the server instead of rubber-banding.
        const velMod = this.client?.getLatestSelf()?.velocityModifier ?? 1;
        const speed =
          baseMobility *
          velMod *
          (wantsWalk ? MOVEMENT_FPS.WALK_SPEED_FACTOR : 1) *
          (wantsCrouch ? MOVEMENT_FPS.CROUCH_SPEED_FACTOR : 1);
        dir.normalize();
        // Capture horizontal velocity for grenade momentum inheritance.
        this.lastVelocity.x = dir.x * speed;
        this.lastVelocity.z = dir.z * speed;
        pos.add(dir.mulScalar(speed * dt));
      }

      // Vertical physics (gravity + jump + ramp surfaces)
      jumpThisFrame =
        (this.jumpRequested || this.keys.jump) && !this.keys.crouch;
      this.integrateVertical(dt, jumpThisFrame, pos);
      this.jumpRequested = false;
      this.lastVelocity.y = this.vy;

      // Wall collision
      this.resolveCollisions(pos);
    } else {
      // Freezetime — clear pending jump latch + zero velocity so the
      // first live tick starts from rest instead of carrying a queued
      // jump or pre-existing fall speed.
      this.jumpRequested = false;
      this.vy = 0;
      // Snap to floor in case the player was airborne at the moment of
      // phase change (between_rounds after a fall).
      const surfaceY = this.getSurfaceY(pos);
      if (pos.y > surfaceY) pos.y = surfaceY;
    }
    this.body.setPosition(pos);

    if (this.client) {
      const movingKeys = this.keys.forward || this.keys.back || this.keys.left || this.keys.right;

      // Build SHOOT flag: edge-triggered for semi, sustained for auto.
      // We look up the equipped weapon's `auto` flag from shared/weapons.
      const self = this.client.getLatestSelf();
      const equippedId = self?.weaponId ?? "g18";
      let isAuto = false;
      let hasMag = true;
      let isGrenade = false;
      let isMelee = false;
      try {
        const ws = getWeapon(equippedId);
        isAuto = !!ws.auto;
        hasMag = ws.magSize > 0;
        isGrenade = ws.class === "grenade";
        isMelee = ws.class === "melee";
      } catch {
        /* unknown id — treat as semi */
      }
      // Remember the last non-melee weapon so Q can swap back to it.
      if (!isMelee && equippedId !== "knife") {
        this.lastNonKnifeWeaponId = equippedId;
      }
      // Local ammo gate: don't even SEND a shoot input (and skip muzzle
      // flash + recoil) when the magazine is empty or a reload is in
      // progress. The server will reject the shot anyway, but the client
      // would otherwise still play the visual fire effects — making it
      // look like bullets fly while ammo stays at 0.
      // Grenades take a separate code path (charge + throw) handled by
      // GrenadeController, so we never set the SHOOT bit for them.
      const magAmmo = self?.magAmmo ?? 0;
      const reserveAmmo = self?.reserveAmmo ?? 0;
      const magOk = !hasMag || magAmmo > 0;
      const reloading = !!self?.reloading;

      // RPM cooldown — keep client perfectly in lockstep with the
      // server's `Hit.minTicksBetweenShots(rpm)` so spam-clicking a
      // semi-auto pistol doesn't fire more visual+input frames than
      // the server will accept. Without this gate, the client would
      // emit a SHOOT bit (+ play muzzle flash via onLocalFire) on
      // every fireEdge / fireHeld frame, while the server discards
      // shots that fall inside the cooldown — the mag decrements far
      // less than the user clicks, and the UI feels desynced.
      const equippedRpm = (() => {
        try { return getWeapon(equippedId).rpm; } catch { return 0; }
      })();
      const minTicks = equippedRpm > 0
        ? Math.max(4, Math.ceil((60 / equippedRpm) * 64))
        : 32;
      const minGapMs = ((minTicks + 1) / 64) * 1000;
      const cooldownOk = performance.now() - this.lastFireMs >= minGapMs;

      // Sprint 5 — view-model busy gate (switch / inspect). Blocks
      // SHOOT + knife edges only; reloads still queue so the player can
      // queue a reload while switching.
      const viewBlocked = !!this.inputBlocker?.();

      // Freezetime gate — weapons are inert until the round actually starts.
      // Firing and knife attacks are allowed in `live` / `warmup` AND (while
      // alive) the post-round `between_rounds` window — after the kill the
      // winner keeps shooting into the aftermath instead of the gun cutting
      // out the instant the round is decided. The server honors those shots
      // (a post-round death routes through registerLateDeath, never
      // re-deciding the round). During `buy` (and the VS `intro`) the player
      // can still look around and switch/buy guns, but can't shoot or swing
      // (grenade aim is blocked in GrenadeController).
      // Before the first HUD snapshot arrives `getMatchPhase()` is null —
      // treat that as "not live" so combat stays blocked (you're connecting /
      // in the intro / in buy at that point; defaulting open would flash the
      // muzzle during freezetime).
      const combatPhase = this.client.getMatchPhase();
      const phaseAllowsCombat =
        combatPhase === "live" ||
        combatPhase === "warmup" ||
        (combatPhase === "between_rounds" && (self?.health ?? 0) > 0);

      const wantsShoot =
        !isGrenade && !isMelee && !viewBlocked && phaseAllowsCombat &&
        this.locked && magOk && !reloading && cooldownOk &&
        (isAuto ? this.fireHeld : this.fireEdge);

      let actionFlags = 0;
      if (wantsShoot) actionFlags |= ACTION.SHOOT;
      // Scoped state rides on every input frame — the server drops the
      // sniper noscope penalty only when the firing frame carries ZOOM.
      // Set BEFORE the SHOOT block below so the scoped shot itself is
      // accurate even though it immediately unscopes for the bolt.
      if (this.zoomLevel > 0) actionFlags |= ACTION.ZOOM;

      // Auto-reload logic: if mag is empty, and we have reserve ammo, and we aren't already reloading
      const autoReload = hasMag && magAmmo === 0 && reserveAmmo > 0 && !reloading;
      if (this.reloadEdge || autoReload) {
        actionFlags |= ACTION.RELOAD;
      }
      // Knife edges only count when knife is actually equipped — guards
      // against a buffered RMB carrying over after a weapon switch.
      if (isMelee && this.locked && !viewBlocked && phaseAllowsCombat) {
        if (this.knifeSlashEdge) actionFlags |= ACTION.KNIFE_SLASH;
        if (this.knifeStabEdge) actionFlags |= ACTION.KNIFE_STAB;
      }

      if (actionFlags & ACTION.SHOOT) {
        // We already gated SHOOT itself on the RPM cooldown above, so
        // stamping lastFireMs + applying recoil here is unconditional —
        // every SHOOT bit emitted is one server-honored shot.
        this.lastFireMs = performance.now();
        this.applySprayKick(equippedId);
        // AWP bolt cycle — a scoped shot drops the scope and re-zooms
        // to the same level once the bolt finishes (CS:GO behavior).
        if (this.zoomLevel > 0 && this.isSniperEquipped()) {
          this.pendingRezoomLevel = this.zoomLevel;
          this.zoomLevel = 0;
          this.rezoomAtMs =
            performance.now() + FirstPersonController.REZOOM_DELAY_MS;
        }
        // Refresh body/camera Euler immediately so the tracer spawned
        // via `onLocalFire` below (inside sendInput) reads cam.forward
        // including THIS shot's spray pattern offset. Without this the
        // tracer is one pattern step behind the server's bullet —
        // user sees the tracer fly in the previous shot's direction
        // while the bullet actually goes where the NEW pattern entry
        // points. Matches FpsArena's `applyAccuracyAndSpray` which
        // applies the pattern offset for the current spray index.
        const newTotalRecoilPitch = this.recoilPitch + this.patternPitch + shakePitch;
        const newTotalRecoilYaw = this.recoilYaw + this.patternYaw + shakeYaw;
        // Same separation as the top-of-frame Euler: body stays at
        // pure `this.yaw` (so movement direction never picks up the
        // pattern/recoil/shake yaw), camera takes the offsets locally.
        this.body.setEulerAngles(0, this.yaw, 0);
        this.camera.setLocalEulerAngles(this.pitch + newTotalRecoilPitch, newTotalRecoilYaw, 0);
      }
      const keepPendingSemiFire =
        !isAuto &&
        this.fireEdge &&
        !(actionFlags & ACTION.SHOOT) &&
        !isGrenade &&
        !isMelee &&
        this.locked &&
        phaseAllowsCombat &&
        magOk &&
        !reloading &&
        !cooldownOk;
      // Knife lunge — small forward pitch dip on slash/stab. Pure
      // visual feedback; Sprint 5 will replace this with view-model
      // animation, but until then a tiny camera kick reads as a swing.
      if ((actionFlags & ACTION.KNIFE_SLASH) || (actionFlags & ACTION.KNIFE_STAB)) {
        const isStab = !!(actionFlags & ACTION.KNIFE_STAB);
        this.recoilPitch += isStab ? -1.4 : -0.6;
        this.recoilYaw += (Math.random() - 0.5) * (isStab ? 0.8 : 0.4);
      }

      // Input send batching: the server ticks at 64 Hz and coalesces
      // whatever arrives between ticks (latest-wins), so sending faster
      // than 64 Hz is pure message overhead. Combat edges (they always
      // set an action flag) and fresh jump presses flush IMMEDIATELY so
      // action latency is untouched; movement/look state rides the ≤64 Hz
      // cadence. On skipped frames the edge latches + accumulated mouse
      // deltas are left alone — they carry into the next sent frame.
      const urgent =
        (actionFlags &
          (ACTION.SHOOT | ACTION.RELOAD | ACTION.KNIFE_SLASH | ACTION.KNIFE_STAB)) !==
          0 || this.jumpUrgent;
      const nowMs = performance.now();
      if (
        urgent ||
        nowMs - this.lastInputSendMs >= FirstPersonController.INPUT_SEND_INTERVAL_MS
      ) {
        this.lastInputSendMs = nowMs;
        this.jumpUrgent = false;
        const frame: InputFrame = {
          seq: ++this.inputSeq,
          forward: this.keys.forward,
          back:    this.keys.back,
          left:    this.keys.left,
          right:   this.keys.right,
          jump:    jumpThisFrame,
          crouch:  this.keys.crouch,
          // Scoped counts as walking — server slows movement + the client
          // speed calc above applies the same factor (prediction parity).
          walk:    (this.keys.walk || this.isScoped()) && !!movingKeys,
          // Absolute, client-authoritative aim. Always sent (even unlocked —
          // it's the current angle, idempotent on the server). This is what
          // the server now uses for yaw/pitch; the deltas below are legacy.
          yaw:     this.yaw,
          pitch:   this.pitch,
          dPitch:  this.locked ? this.accDPitch : 0,
          dYaw:    this.locked ? this.accDYaw   : 0,
          actionFlags,
        };
        this.client.sendInput(frame);
        // Record the predicted position at this seq — the anchor the
        // server's `lastProcessedInputSeq` ack is reconciled against.
        this.predHistory.push({ seq: frame.seq, x: pos.x, y: pos.y, z: pos.z });
        if (this.predHistory.length > FirstPersonController.PRED_HISTORY_MAX) {
          this.predHistory.splice(
            0,
            this.predHistory.length - FirstPersonController.PRED_HISTORY_MAX,
          );
        }
        if (this.locked) { this.accDPitch = 0; this.accDYaw = 0; }
        // Clear edge-triggered flags after sending.
        this.fireEdge = keepPendingSemiFire;
        this.reloadEdge = false;
        this.knifeSlashEdge = false;
        this.knifeStabEdge = false;
      }
    }
  }

  // ── Server sync ────────────────────────────────────────────────────────────

  setServerPosition(x: number, y: number, z: number): void {
    if (!this.hasInitialServerPos) {
      this.body.setPosition(x, y, z);
      this.vy          = 0;
      this.coyote      = MOVEMENT_FPS.COYOTE_SEC;
      this.prevJumpHeld = false;
      this.hasInitialServerPos = true;
    }
  }

  /**
   * Reconcile local prediction against an authoritative snapshot (32 Hz),
   * ANCHORED on the server's `lastProcessedInputSeq` ack.
   *
   * The server position in a snapshot reflects inputs up to seq N — a
   * state from ~(RTT + snapshot interval) in the past. Comparing it
   * against the CURRENT predicted position (the pre-fix behavior) made
   * the measured "drift" proportional to `speed × latency`, so the soft
   * lerp dragged a sprinting player backwards on every snapshot (rubber-
   * banding) and ping spikes tripped the hard-snap teleport ("movement
   * returns"). Instead we compare the server position against OUR
   * RECORDED prediction at that same seq (`predHistory`):
   *
   *   - error ≤ deadzone → prediction was right; touch nothing. This is
   *     the hot path in normal play at ANY ping.
   *   - real divergence (collision seam, tagging latency, server push) →
   *     correct a fraction per snapshot, capped per step, and shift the
   *     in-flight history by the same delta so follow-up acks measure
   *     the residual error, not the already-applied part.
   *   - teleport-scale error → hard snap + invalidate in-flight history.
   *
   * Y keeps its special cases: mid-air the local arc is trusted (only a
   * large safety threshold overrides); grounded Y corrections mirror the
   * original thresholds but anchored on the acked seq.
   */
  reconcileFromServer(x: number, y: number, z: number, ackSeq: number): void {
    if (!this.hasInitialServerPos) return;
    // Acks that predate a teleport (respawn / hard snap) compare against
    // pre-teleport history — skip them; fresh acks arrive within ~RTT.
    if (ackSeq <= this.ignoreAcksThroughSeq) return;

    const p = this.body.getPosition();

    // Find + consume history up to (and including) the acked seq.
    let anchor: { seq: number; x: number; y: number; z: number } | null = null;
    while (this.predHistory.length > 0 && this.predHistory[0].seq <= ackSeq) {
      const e = this.predHistory.shift()!;
      if (e.seq === ackSeq) anchor = e;
    }

    if (!anchor) {
      // No anchor (history overflow / duplicate ack after a hitch). Never
      // soft-lerp against a stale position — that's the rubber-band bug.
      // Keep only a runaway-desync safety net.
      const dx = x - p.x;
      const dz = z - p.z;
      if (dx * dx + dz * dz > 4 * 4) this.hardSnapTo(x, y, z);
      return;
    }

    const errX = x - anchor.x;
    const errY = y - anchor.y;
    const errZ = z - anchor.z;
    const errHSq = errX * errX + errZ * errZ;
    const {
      RECONCILE_DEADZONE_M: DEAD,
      RECONCILE_CORRECT_FRACTION: FRAC,
      RECONCILE_MAX_STEP_M: MAX_STEP,
      RECONCILE_SNAP_M: SNAP,
    } = FirstPersonController;

    if (errHSq > SNAP * SNAP) {
      this.hardSnapTo(x, y, z);
      return;
    }

    // ── Horizontal correction ──────────────────────────────────────
    let applyX = 0;
    let applyZ = 0;
    if (errHSq > DEAD * DEAD) {
      const err = Math.sqrt(errHSq);
      const step = Math.min(err * FRAC, MAX_STEP);
      applyX = (errX / err) * step;
      applyZ = (errZ / err) * step;
    } else if (errHSq > 0.02 * 0.02) {
      // Inside the deadzone: bleed residual drift only while IDLE +
      // grounded so the standing player's client/server positions fully
      // converge (bullets trace from the server pose), without ever
      // tugging against active movement.
      const idle =
        !this.keys.forward && !this.keys.back && !this.keys.left && !this.keys.right;
      if (idle && Math.abs(this.vy) < 0.15) {
        applyX = errX * 0.1;
        applyZ = errZ * 0.1;
      }
    }

    // ── Vertical correction (grounded only, anchored) ──────────────
    let applyY = 0;
    const surfaceY = this.getSurfaceY(p);
    const { GROUND_EPS } = MOVEMENT_FPS;
    const airborne = p.y > surfaceY + GROUND_EPS + 0.04 || Math.abs(this.vy) > 0.15;
    if (airborne) {
      // Mid-air the local arc is authoritative for feel; only a large
      // anchored divergence (missed teleport / elevator-style push)
      // overrides it.
      if (Math.abs(errY) > 1.5) {
        this.hardSnapTo(x, y, z);
        return;
      }
    } else if (errY <= 0.10) {
      // Grounded. Upward corrections > 0.10 m are a jump/landing cadence
      // skew (≤1 tick) — ignore; both sides settle on the same surface.
      if (Math.abs(errY) > 0.45) {
        p.y = y;
        if (p.y <= surfaceY + GROUND_EPS) this.vy = 0;
        this.body.setPosition(p);
      } else if (Math.abs(errY) > 0.012) {
        applyY = errY * 0.24;
      }
    }

    if (applyX !== 0 || applyY !== 0 || applyZ !== 0) {
      p.x += applyX;
      p.y += applyY;
      p.z += applyZ;
      this.body.setPosition(p);
      // Shift the in-flight prediction history by the applied correction
      // so the next ack measures the REMAINING error instead of re-seeing
      // (and re-applying) the part we just corrected.
      for (const e of this.predHistory) {
        e.x += applyX;
        e.y += applyY;
        e.z += applyZ;
      }
    }
  }

  /** Teleport-scale reconcile: adopt the server position outright and
   *  invalidate all in-flight prediction history (acks for inputs sent
   *  before this moment must not be compared against it). */
  private hardSnapTo(x: number, y: number, z: number): void {
    this.body.setPosition(x, y, z);
    this.vy = 0;
    this.predHistory.length = 0;
    this.ignoreAcksThroughSeq = this.inputSeq;
  }

  syncYawFromServer(yaw: number): void {
    if (!this.hasInitialServerPos) this.yaw = yaw;
  }

  isLocked(): boolean          { return this.locked; }
  isPointerLocked(): boolean   { return this.locked; }
  getCameraEntity(): pc.Entity { return this.camera; }
  /** The body root (follows position + yaw, never pitch). Used to anchor
   *  the local shadow-only body. */
  getBodyEntity(): pc.Entity { return this.body; }
  /** True once on the frame a jump fires (then cleared). Lets the shadow
   *  play the jump clip the instant Space is pressed, not when airborne. */
  consumeJustJumped(): boolean { const j = this.justJumped; this.justJumped = false; return j; }
  /** True once on the frame the local player lands from a jump/fall with real
   *  impact speed (then cleared). Drives the one-shot landing sound. */
  consumeJustLanded(): boolean { const j = this.justLanded; this.justLanded = false; return j; }

  /** True while the player holds a "move quietly" modifier — shift-walk,
   *  crouch, or scoped (the same set that silences footsteps). The audio
   *  layer reads this on touchdown to soften the landing thud when the
   *  player drops off a ledge while deliberately sneaking, instead of a
   *  full-volume slam. */
  isQuietMovement(): boolean {
    return this.keys.walk || this.keys.crouch || this.isScoped();
  }

  /**
   * True when the local player is making footstep noise: moving on the
   * ground at run speed. Silent when shift-walking, crouching, airborne,
   * or while movement is locked (freezetime / not pointer-locked).
   * Drives the local footstep loop in the Footsteps audio manager.
   */
  isFootstepActive(): boolean {
    if (!this.locked) return false;
    const phase = this.client?.getMatchPhase() ?? "live";
    if (phase !== "live" && phase !== "warmup") return false;
    const moving = this.keys.forward || this.keys.back || this.keys.left || this.keys.right;
    if (!moving) return false;
    // Shift / crouch = silent; scoped moves at walk speed so it's silent too.
    if (this.keys.walk || this.keys.crouch || this.isScoped()) return false;
    const pos = this.body.getPosition();
    const grounded =
      pos.y <= this.getSurfaceY(pos) + MOVEMENT_FPS.GROUND_EPS + 0.06 &&
      Math.abs(this.vy) < 0.3;
    return grounded;
  }
  /**
   * Current body velocity (m/s): horizontal movement intent + vertical
   * physics, zero while movement is locked. Read by GrenadeController so
   * thrown grenades inherit player momentum — CS-style running throws and
   * jump throws. Returns a live internal vector; do not mutate.
   */
  getVelocity(): pc.Vec3 { return this.lastVelocity; }

  /** True while LMB is depressed — read by GrenadeController for charge state. */
  isFireHeld(): boolean        { return this.fireHeld; }
  /** Tells the controller "I'm handling this LMB press, don't fire a shot." */
  consumeFireEdge(): void      { this.fireEdge = false; }

  /** Authoritative respawn between rounds (server snapshot / round live). */
  respawnAt(x: number, y: number, z: number, yaw: number, pitch = 0): void {
    this.body.setPosition(x, y, z);
    this.yaw = yaw;
    // Reset pitch to the server's authoritative value (it sets
    // p.pitch = 0 on respawn). The client used to keep its previous
    // pitch while the server reset to 0, so player.pitch drifted below
    // this.pitch every round. Shots fire from player.pitch, so the
    // bullet climbed above the crosshair — and the player compensated
    // by aiming down, pushing the carried pitch further negative and
    // making the gap grow each round (very visible by ~round 3:
    // aiming at the floor hit the enemy / ceiling).
    this.pitch = pitch;
    // Drop any in-flight visual recoil / spray kick so it doesn't bias
    // the first frame after the reset.
    this.recoilPitch = 0;
    this.recoilYaw = 0;
    this.patternPitch = 0;
    this.patternYaw = 0;
    // Drop unsent look deltas too. With absolute aim the server ignores
    // these, but a legacy (not-yet-redeployed) server would still integrate
    // the leftover delta onto its freshly-reset spawn yaw, re-creating a
    // small constant offset. Clearing them keeps the rebase clean either way.
    this.accDYaw = 0;
    this.accDPitch = 0;
    // Reset the spray INDEX + bloom too — the server zeroes spray state on
    // respawn (resetSpray), so if a round ended mid-spray the client must
    // also restart at pattern[0] or its camera pattern offset (crosshair)
    // would track a later pattern entry than the server's bullet for the
    // first shots of the new round.
    this.sprayIndex = 0;
    this.sprayInaccDeg = 0;
    this.lastWeaponIdForSpray = null;
    // New round starts unscoped — matches the server (no ZOOM flag yet).
    this.resetZoom();
    // Respawn is a teleport: every in-flight input was predicted from the
    // PRE-respawn position, so acks for them must not be reconciled against
    // the new one (they'd read as a huge bogus error and re-snap us back).
    this.predHistory.length = 0;
    this.ignoreAcksThroughSeq = this.inputSeq;
    // Fresh spawn stands on its floor at rest.
    this.vy = 0;
    this.coyote = 0;
    this.lastVelocity.set(0, 0, 0);
    this.body.setEulerAngles(0, this.yaw, 0);
    this.camera.setLocalEulerAngles(this.pitch, 0, 0);
    this.hasInitialServerPos = true;
  }

  /**
   * Slot switcher (1/2/3/4). Resolves the slot to a weapon id by
   * filtering the player's `ownedWeapons` list against weapon class:
   *   1 → rifle/sniper (preferring rifle if both owned)
   *   2 → pistol
   *   3 → knife
   *   4 → grenade (cycles HE → flash → smoke → molotov on repeat presses)
   * No-op when the player owns nothing in that slot.
   */
  equipSlot(slot: 1 | 2 | 3 | 4): void {
    if (!this.client) return;
    const self = this.client.getLatestSelf();
    if (!self) return;
    const owned = self.ownedWeapons ?? ["knife", "g18"];
    const currentId = self.weaponId;

    const idsByClass = (cls: string): string[] => {
      const out: string[] = [];
      for (const id of owned) {
        try {
          if (getWeapon(id).class === cls) out.push(id);
        } catch { /* unknown id — ignore */ }
      }
      return out;
    };

    let target: string | undefined;
    if (slot === 1) {
      // Primary slot — the owned rifle / SMG / sniper. Resolve through the
      // canonical slot map (not a hardcoded class list) so SMGs count too:
      // the old `rifle ?? sniper` lookup skipped `smg`, so after switching
      // away from a MAC-10 / MP9 pressing 1 found nothing and did nothing.
      target = owned.find((id) => weaponSlotFor(id) === "primary");
    } else if (slot === 2) {
      target = idsByClass("pistol")[0];
    } else if (slot === 3) {
      target = idsByClass("melee")[0] ?? "knife";
    } else if (slot === 4) {
      // Cycle through owned grenades. If currently holding a grenade,
      // pick the next one in the list; otherwise pick the first.
      const grenades = idsByClass("grenade");
      if (grenades.length === 0) return;
      const idx = grenades.indexOf(currentId);
      target = grenades[(idx + 1) % grenades.length];
    }
    if (!target || target === currentId) return;
    this.client.sendWeaponEquip(target);
  }

  /**
   * Sprint 3 — apply spray pattern (or legacy random kick) on a shot.
   *
   * For weapons with a deterministic `sprayPattern` (AK, M4 family), the
   * crosshair kicks to the cumulative offset described in the spec — the
   * player must learn and counter the pattern just like in CS:GO. For
   * pistols/AWP/SMG we keep the legacy random kick since those don't
   * have learnable patterns. Spray state mirrors FpsArena exactly.
   */
  private applySprayKick(weaponId: string): void {
    if (this.lastWeaponIdForSpray !== weaponId) {
      this.lastWeaponIdForSpray = weaponId;
      this.sprayIndex = 0;
      this.sprayInaccDeg = 0;
    }

    let pattern: ReadonlyArray<readonly [number, number]> | null = null;
    let profile = "low";
    try {
      const spec = getWeapon(weaponId);
      pattern = spec.sprayPattern;
      profile = spec.recoilProfile;
    } catch {
      /* unknown weapon — fall through to default kick */
    }

    if (pattern && pattern.length > 0) {
      // Cumulative offsets — set patternPitch/patternYaw to the absolute
      // pattern position so visual recoil tracks the deterministic kick
      // (held while spraying, decays after 400 ms idle). Spec uses
      // screen-right-positive yaw; our convention is yaw+ = left.
      const idx = Math.min(this.sprayIndex, pattern.length - 1);
      const [pPitch, pYaw] = pattern[idx];
      this.patternPitch = pPitch;
      this.patternYaw = -pYaw;
      this.sprayIndex = Math.min(this.sprayIndex + 1, pattern.length - 1);
    }
    // Non-pattern weapons (pistols / AWP / SMG / shotgun / knife):
    // intentionally NOT applying any `recoilPitch`/`recoilYaw` to the
    // camera. The previous code added a per-shot pitch kick (up to 7.5°
    // for Deagle) but the server does NOT apply that offset to the
    // bullet — server bullet direction is `player.pitch + 0` plus the
    // Gaussian inaccuracy. The visual camera kick made the crosshair
    // pop upward while the bullet stayed at the original aim → "mira
    // está em um lugar e a bala vai para outro".
    //
    // The per-shot bullet spread for these weapons comes from
    // `inaccuracyDeg` (the Gaussian cone), which is consistent between
    // client (crosshair gap) and server (bullet ray). The gun model's
    // own kickback animation in `LocalWeaponView` (kickbackOffset,
    // kickbackRotX, kickbackRotZ) still plays so the player gets
    // tactile recoil feedback — just on the weapon model, not the
    // camera, so it never lies about where the bullet went.

    this.sprayInaccDeg += FirstPersonController.SPRAY_INACC_PER_SHOT;
    this.lastShotForSprayMs = performance.now();
  }

  /**
   * Sprint 3 — compute the local-player inaccuracy cone (deg) for the
   * crosshair. Mirrors the server's `computeInaccuracy` formula. The
   * decay + reset are advanced inside `update()` via dt.
   */
  private updateInaccuracy(dt: number): void {
    const equippedId = this.client?.getLatestSelf()?.weaponId ?? "g18";

    // Weapon switch detected mid-spray — drop all spray state so the
    // crosshair doesn't keep the previous weapon's bloom and the camera
    // stops holding the AK pattern after the player swapped to a knife.
    if (this.lastWeaponIdForSpray !== null && this.lastWeaponIdForSpray !== equippedId) {
      this.sprayIndex = 0;
      this.sprayInaccDeg = 0;
      this.patternPitch = 0;
      this.patternYaw = 0;
      this.lastWeaponIdForSpray = equippedId;
    }

    // Decay spray bloom.
    if (this.sprayInaccDeg > 0) {
      this.sprayInaccDeg = Math.max(0, this.sprayInaccDeg - FirstPersonController.SPRAY_INACC_DECAY * dt);
    }
    // Reset spray index after 400 ms of no fire.
    if (performance.now() - this.lastShotForSprayMs > 400 && this.sprayIndex > 0) {
      this.sprayIndex = 0;
    }
    let inacc = 0;
    try {
      const spec = getWeapon(equippedId);
      inacc = spec.inaccuracyStanding;

      const moving =
        (this.keys.forward !== this.keys.back) ||
        (this.keys.left !== this.keys.right);
      let speedFraction = 0;
      if (moving) {
        speedFraction = 1.0;
        // Scoped forces the walk flag on the wire — mirror it here so the
        // local cone matches the server's `input.walk` computation.
        if (this.keys.walk || this.isScoped()) speedFraction *= MOVEMENT_FPS.WALK_SPEED_FACTOR;
        if (this.keys.crouch) speedFraction *= MOVEMENT_FPS.CROUCH_SPEED_FACTOR;
      }
      inacc += speedFraction * spec.mobilityFactor * 3.0;
      inacc += this.sprayInaccDeg;

      // Sniper fired from the hip (noscope) — huge cone, like CS:GO.
      // Mirrors FpsArena.computeInaccuracy's ACTION.ZOOM check.
      if (spec.class === "sniper" && this.zoomLevel === 0) {
        inacc += FirstPersonController.SNIPER_NOSCOPE_INACC;
      }

      if (this.keys.crouch) inacc *= 0.7;

      // Airborne — vy != 0 or feet clearly above the surface the
      // player is over (crate top / catwalk / mid platform / floor).
      // Pre-fix this compared against FLOOR_Y so standing on a crate
      // counted as airborne (×4 inaccuracy) just for being elevated.
      const bodyPos = this.body.getPosition();
      const airborne =
        Math.abs(this.vy) > 0.15 ||
        bodyPos.y > this.getSurfaceY(bodyPos) + 0.12;
      if (airborne) inacc *= 4;

      // First-shot accuracy: ≥400 ms idle + stationary + grounded.
      const idle = performance.now() - this.lastShotForSprayMs > 400;
      if (idle && !moving && !airborne) inacc *= 0.4;
    } catch {
      /* unknown weapon — leave inacc at 0 */
    }

    this.inaccuracyDeg = inacc;
  }

  /** Read-only access for the dynamic crosshair UI. Sprint 3. */
  getInaccuracyDeg(): number { return this.inaccuracyDeg; }

  /**
   * Sprint 5 — register the shared camera shake bus
   * (`GrenadeEffects.consumeShake`). FPS applies the offsets to the
   * camera each frame. Passing `null` detaches.
   */
  setShakeSource(src: { consumeShake(): { pitch: number; yaw: number } } | null): void {
    this.shakeSource = src;
  }

  /**
   * Sprint 5 — register a predicate that returns true while combat
   * input (SHOOT / KNIFE_SLASH / KNIFE_STAB) should be ignored. Used to
   * lock fire input during weapon switch / inspect animations.
   */
  setInputBlocker(fn: (() => boolean) | null): void {
    this.inputBlocker = fn;
  }

  /** Sprint 5 — expose movement intent so the view-model bob state machine reads it live. */
  getMovementState(): { moving: boolean; walking: boolean; crouching: boolean; airborne: boolean } {
    const moving =
      (this.keys.forward !== this.keys.back) ||
      (this.keys.left !== this.keys.right);
    return {
      moving,
      walking: !!this.keys.walk && moving,
      crouching: !!this.keys.crouch,
      airborne: Math.abs(this.vy) > 0.15 || this.body.getPosition().y > MOVEMENT_FPS.FLOOR_Y + 0.12,
    };
  }

  /** Per-shot spray bloom increment (degrees). Mirrors FpsArena. */
  private static readonly SPRAY_INACC_PER_SHOT = 0.5;
  /** Spray bloom decay rate (degrees per second). Mirrors FpsArena. */
  private static readonly SPRAY_INACC_DECAY = 4.0;
  /**
   * Extra inaccuracy (deg) when a sniper fires without the scope.
   * Mirrors FpsArena.SNIPER_NOSCOPE_INACC — keep in sync.
   */
  private static readonly SNIPER_NOSCOPE_INACC = 8.0;
  /** Bolt-cycle delay before the scope re-zooms after a scoped shot (ms). */
  private static readonly REZOOM_DELAY_MS = 900;
}
